import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { InventoryFilters } from '@/lib/api/client/inventory'
import { getYmEconomicsStats } from '@/lib/api/client/ymarket-pricing'
import type { YmEconomicsStats } from '@/lib/api/schemas/ymarket-pricing.schema'
import type { WarehouseSelection } from '@/hooks/useWarehouseSelection'
import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { InventorySelectionAction, SelectionScope } from '@/lib/views/actions'
import { selectionFilterBody } from '@/lib/warehouse/selection-filter'

const STATS_DEBOUNCE_MS = 250

function statsKey(scope: SelectionScope<InventoryFilters>): string {
  return scope.kind === 'ids'
    ? `ids:${[...scope.ids].sort().join(',')}`
    : `filter:${scope.expectedTotal}:${JSON.stringify(selectionFilterBody(scope.filter))}`
}

export function WarehouseSelectionBar({
  selection,
  actions,
  rows,
  onActionDone,
  onStatsChange,
  onHeightChange,
}: {
  selection: WarehouseSelection
  actions: readonly InventorySelectionAction[]
  rows?: readonly InventoryListItem[]
  onActionDone?: () => void
  onStatsChange?: (stats: YmEconomicsStats | null) => void
  /** The bar's height, so the list can end above it instead of under it; 0 while it is hidden. */
  onHeightChange?: (height: number) => void
}) {
  const [openAction, setOpenAction] = useState<InventorySelectionAction | null>(null)
  const [stats, setStats] = useState<YmEconomicsStats | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const selectionScope = selection.scope
  const key = selectionScope === null ? null : statsKey(selectionScope)
  // The scope object changes identity on every render; its content key decides refetching.
  const scopeRef = useRef(selectionScope)
  scopeRef.current = selectionScope

  useEffect(() => {
    setStats(null)
    setRefusal(null)
    const scope = scopeRef.current
    if (key === null || scope === null) return undefined
    const controller = new AbortController()
    // The summary asks about exactly the selection an action would receive.
    const request = scope.kind === 'ids'
      ? { ids: [...scope.ids] }
      : { filter: selectionFilterBody(scope.filter), expectedTotal: scope.expectedTotal }
    const timer = window.setTimeout(() => {
      getYmEconomicsStats(request, controller.signal)
        .then((next) => { if (!controller.signal.aborted) setStats(next) })
        .catch((reason: unknown) => {
          if (controller.signal.aborted) return
          setStats(null)
          // The server's own sentence: a refused filter selection must not vanish silently.
          setRefusal(reason instanceof Error ? reason.message : 'Не удалось посчитать выбор.')
        })
    }, STATS_DEBOUNCE_MS)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [key])

  useEffect(() => {
    onStatsChange?.(stats)
  }, [onStatsChange, stats])

  const barRef = useRef<HTMLDivElement | null>(null)
  const visible = selection.selectedCount > 0 && selectionScope !== null
  useLayoutEffect(() => {
    const bar = barRef.current
    if (!visible || bar === null) {
      onHeightChange?.(0)
      return undefined
    }
    const report = () => onHeightChange?.(Math.ceil(bar.getBoundingClientRect().height))
    report()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(report)
    observer.observe(bar)
    return () => observer.disconnect()
  }, [onHeightChange, visible])

  if (!visible || selectionScope === null) return null

  const refused = selection.refused

  return (
    <>
      <div
        ref={barRef}
        data-testid="warehouse-selection-bar"
        className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[env(safe-area-inset-bottom)] sm:px-5"
      >
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-2 rounded-lg border border-rule-strong bg-onyx px-3 py-2 text-ivory shadow-[0_-8px_30px_rgba(20,17,13,0.18)]">
          <output aria-live="polite" className="mr-auto text-sm font-medium">
            Выбрано: {selection.selectedCount.toLocaleString('ru-RU')}{refused !== null ? ` · ещё ${refused.count.toLocaleString('ru-RU')} нельзя выбрать` : ''}
          </output>
          <button type="button" className="btn btn-sm border border-white/25 text-ivory hover:bg-white/10" onClick={selection.clear}>
            Снять выделение
          </button>
          {actions.map((action) => (
            <button key={action.key} type="button" className="btn btn-sm bg-gold text-ivory hover:bg-gold-deep" onClick={() => setOpenAction(action)}>
              {action.label}
            </button>
          ))}
          {refused !== null ? (
            <p data-testid="warehouse-selection-refused" className="basis-full text-xs text-ivory/85">
              Нельзя выбрать: {refused.reasons.map((item) => `${item.reason}: ${item.count.toLocaleString('ru-RU')}`).join('; ')}
            </p>
          ) : null}
          {refusal !== null ? <p role="alert" className="basis-full border-t border-white/15 pt-1 text-xs text-gold-soft">{refusal}</p> : null}
        </div>
      </div>
      {openAction ? <openAction.Dialog selection={selectionScope} rows={rows} onDone={() => { setOpenAction(null); onActionDone?.() }} /> : null}
    </>
  )
}
